/**
 * Aus den Token erzeugen, was andere brauchen — die Shell und die
 * Fremddienste im Rahmen.
 *
 * ADR-040, Auflage 4: „Kein Theme ohne Quelle. Die Farben stammen aus den
 * Designtoken von `UI-001` und werden aus ihnen erzeugt. Eine zweite, von Hand
 * gepflegte Farbliste läuft auseinander."
 *
 * Diese Datei ist die Umsetzung dieses Satzes. Sie enthält **keine einzige
 * Farbe** — sie liest sie aus `token.ts`. Wer hier einen Hex-Wert einträgt,
 * hat die zweite Liste angefangen, gegen die sich die Auflage richtet; der
 * Test daneben prüft genau das.
 */

import {
  MASS,
  SCHRIFT,
  farbsatz,
  type Erscheinung,
  type Farbsatz,
} from "./token.js";

/** Präfix aller CSS-Variablen. Kurz, aber eindeutig genug im Fremdrahmen. */
const PRAEFIX = "--hwc";

/**
 * CSS-Variablen für die Shell.
 *
 * Ausgegeben wird nur der Inhalt, nicht der Selektor: Wo sie landen — `:root`,
 * ein Datenattribut je Erscheinung, ein Shadow Root — entscheidet die Shell.
 */
/**
 * Was ein Kundenstack an Farbe beisteuern darf.
 *
 * `undefined` steht ausdrücklich dabei: Bei `exactOptionalPropertyTypes` ist
 * „Feld fehlt" nicht dasselbe wie „Feld ist undefined", und der Aufrufer baut
 * das Objekt aus einer Konfiguration, in der beides vorkommt.
 */
export interface Markenfarben {
  readonly akzent?: string | undefined;
  readonly aufAkzent?: string | undefined;
}

export function cssVariablen(
  erscheinung: Erscheinung,
  marke?: Markenfarben,
): string {
  // Die Markenfarbe des Betriebs überschreibt genau zwei Rollen und keine
  // weitere. Fläche, Text und Zustandsfarben bleiben, wie sie geprüft wurden -
  // sonst wäre die Kontrastrechnung aus `kontrast.ts` eine Aussage über etwas,
  // das so nie erscheint. Ob die Marke selbst trägt, prüft `@hwc/config`
  // **beim Anlegen des Kunden**; hier wird sie nur eingesetzt.
  const f: Farbsatz = { ...farbsatz(erscheinung), ...leereWegLassen(marke) };
  const zeilen: string[] = [];

  for (const [name, wert] of Object.entries(f)) {
    zeilen.push(`${PRAEFIX}-farbe-${bindestrich(name)}: ${wert};`);
  }
  for (const [name, wert] of Object.entries(MASS.abstand)) {
    zeilen.push(`${PRAEFIX}-abstand-${name}: ${wert};`);
  }
  for (const [name, wert] of Object.entries(MASS.radius)) {
    zeilen.push(`${PRAEFIX}-radius-${name}: ${wert};`);
  }
  zeilen.push(`${PRAEFIX}-beruehrung: ${MASS.beruehrung};`);
  zeilen.push(`${PRAEFIX}-beruehrung-gross: ${MASS.beruehrungGross};`);
  zeilen.push(`${PRAEFIX}-fokusbreite: ${MASS.fokusbreite};`);
  zeilen.push(`${PRAEFIX}-schrift: ${SCHRIFT.familie};`);
  zeilen.push(`${PRAEFIX}-schrift-fest: ${SCHRIFT.familieFest};`);
  for (const [name, wert] of Object.entries(SCHRIFT.groesse)) {
    zeilen.push(`${PRAEFIX}-schriftgroesse-${name}: ${wert};`);
  }
  zeilen.push(`${PRAEFIX}-zeilenhoehe: ${SCHRIFT.zeilenhoehe};`);

  return zeilen.join("\n");
}

/**
 * Das Theme, das ein Fremddienst im Rahmen bekommt.
 *
 * Bewusst eine **neutrale Zwischenform** und nicht das Format eines bestimmten
 * Dienstes: Bulwark führt eine eigene `themes`-Liste, Vikunja und Nextcloud
 * kennen andere Wege. Die Abbildung auf das jeweilige Format gehört zu dem
 * Paket, das den Dienst ausrollt — `MAIL-002` für Bulwark —, weil erst dort
 * gemessen werden kann, was der Dienst tatsächlich annimmt.
 *
 * Was hier festliegt, ist die **Herkunft**: Ein Dienst bekommt diese Werte
 * oder keine. Er bekommt keine eigenen.
 */
export interface FremdTheme {
  readonly erscheinung: Erscheinung;
  readonly farben: Farbsatz;
  readonly schrift: string;
  readonly radius: string;
}

export function fremdTheme(erscheinung: Erscheinung): FremdTheme {
  return {
    erscheinung,
    farben: farbsatz(erscheinung),
    schrift: SCHRIFT.familie,
    radius: MASS.radius.m,
  };
}

/** Leere Angaben dürfen keinen Token überschreiben. */
function leereWegLassen(marke: Markenfarben | undefined): Partial<Farbsatz> {
  const aus: Record<string, string> = {};
  for (const [k, v] of Object.entries(marke ?? {})) {
    if (typeof v === "string" && v.trim() !== "") aus[k] = v;
  }
  return aus as Partial<Farbsatz>;
}

/** `flaecheErhoben` → `flaeche-erhoben`. */
function bindestrich(name: string): string {
  return name.replace(/[A-Z]/g, (b) => "-" + b.toLowerCase());
}
